import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, CheckCheck, Trash2, X, CreditCard, MapPin, Calendar, Sparkles, Clock, AlertTriangle, UserCheck } from 'lucide-react';
import { useNotificationState } from '../context/NotificationContext';
import { ScrollArea } from './ui/scroll-area';

const ICON_MAP = {
  cleaner_clocked_in: { icon: Clock, color: 'text-green-500', bg: 'bg-green-50' },
  cleaner_clocked_out: { icon: Sparkles, color: 'text-blue-500', bg: 'bg-blue-50' },
  payment_successful: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-50' },
  payment_failed: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
  new_booking: { icon: Calendar, color: 'text-orange-500', bg: 'bg-orange-50' },
  gps_flag: { icon: MapPin, color: 'text-red-500', bg: 'bg-red-50' },
  job_assigned: { icon: UserCheck, color: 'text-purple-500', bg: 'bg-purple-50' },
  appointment_confirmed: { icon: Check, color: 'text-green-500', bg: 'bg-green-50' },
};

const getTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const NotificationBell = () => {
  const { notifications, unreadCount, clearNotifications, markAsRead } = useNotificationState();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkAllRead = () => {
    notifications.forEach(n => {
      if (!n.read) markAsRead(n.id || n.timestamp);
    });
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id || notification.timestamp);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full text-zinc-600 hover:text-[#F56F05] hover:bg-[#FFF7ED] transition-all duration-200"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 ring-2 ring-white"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl shadow-2xl border border-zinc-200/80 overflow-hidden z-50"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50/50">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-zinc-900 text-sm">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="bg-[#F56F05] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-[#F56F05] hover:bg-[#FFF7ED] transition-colors"
                    title="Mark all as read"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="h-4 w-4" />
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearNotifications}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Clear all"
                    data-testid="clear-notifications-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors ml-1"
                  data-testid="close-notification-panel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            {notifications.length === 0 ? (
              <div className="py-12 text-center" data-testid="no-notifications">
                <Bell className="h-10 w-10 text-zinc-200 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm font-medium">No notifications yet</p>
                <p className="text-zinc-300 text-xs mt-1">You'll see updates here in real time</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[360px]">
                <div className="divide-y divide-zinc-50">
                  {notifications.map((notification, idx) => {
                    const iconConfig = ICON_MAP[notification.type] || { icon: Bell, color: 'text-zinc-500', bg: 'bg-zinc-50' };
                    const IconComponent = iconConfig.icon;
                    const isUnread = !notification.read;

                    return (
                      <motion.button
                        key={notification.id || notification.timestamp || idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors hover:bg-zinc-50/80 ${
                          isUnread ? 'bg-[#FFFAF5]' : ''
                        }`}
                        data-testid={`notification-item-${idx}`}
                      >
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${iconConfig.bg} flex items-center justify-center mt-0.5`}>
                          <IconComponent className={`h-4 w-4 ${iconConfig.color}`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm leading-tight ${isUnread ? 'font-semibold text-zinc-900' : 'font-medium text-zinc-600'}`}>
                              {notification.title}
                            </p>
                            {isUnread && (
                              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-[#F56F05] mt-1.5" />
                            )}
                          </div>
                          <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{notification.message}</p>
                          <p className="text-[10px] text-zinc-300 mt-1">{getTimeAgo(notification.timestamp)}</p>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
